/**
 * Chat Enhancement Components for Phase 1
 * - Syntax-highlighted code blocks with copy buttons
 * - Message copy button
 * - Expand/collapse for long messages
 * - Stop generation button
 * - Prompt templates
 * - Inline edit
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Copy, Check, ChevronDown, ChevronUp, Square, Pencil, RefreshCw, RotateCcw, Sparkles, Code2, AlertTriangle, Wrench, BookOpen, X, Search } from 'lucide-react';
import Prism from 'prismjs';
import 'prismjs/themes/prism-tomorrow.css';
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-typescript';
import 'prismjs/components/prism-jsx';
import 'prismjs/components/prism-tsx';
import 'prismjs/components/prism-python';
import 'prismjs/components/prism-bash';
import 'prismjs/components/prism-json';
import 'prismjs/components/prism-css';
import 'prismjs/components/prism-sql';
import 'prismjs/components/prism-yaml';
import 'prismjs/components/prism-markdown';
import 'prismjs/components/prism-java';
import 'prismjs/components/prism-go';
import 'prismjs/components/prism-rust';
import 'prismjs/components/prism-c';
import 'prismjs/components/prism-cpp';
import 'prismjs/components/prism-ruby';
import 'prismjs/components/prism-php';
import 'prismjs/components/prism-swift';
import 'prismjs/components/prism-kotlin';
import 'prismjs/components/prism-docker';
import 'prismjs/components/prism-nginx';
import 'prismjs/components/prism-toml';
import 'prismjs/components/prism-graphql';
import 'prismjs/components/prism-markup';

/* ─── Language alias mapping ─── */
const LANG_ALIASES = {
  js: 'javascript', ts: 'typescript', py: 'python', sh: 'bash', shell: 'bash',
  yml: 'yaml', md: 'markdown', dockerfile: 'docker', html: 'markup',
  xml: 'markup', svg: 'markup', htm: 'markup', rb: 'ruby', kt: 'kotlin',
  rs: 'rust', env: 'bash', txt: 'text', text: 'text', plaintext: 'text',
};

function resolveLang(lang) {
  if (!lang) return 'text';
  const lower = lang.toLowerCase().trim();
  return LANG_ALIASES[lower] || lower;
}

/* ─── Copy Button (reusable) ─── */
export function CopyButton({ text, size = 13, label, style = {} }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback((e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [text]);

  return (
    <button
      onClick={handleCopy}
      data-testid="copy-btn"
      title={copied ? 'Copied!' : (label || 'Copy')}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
        padding: '0.1875rem 0.4375rem', borderRadius: '0.25rem', border: 'none',
        background: copied ? 'rgba(16,185,129,0.15)' : 'rgba(255,255,255,0.06)',
        color: copied ? '#10b981' : '#94a3b8',
        cursor: 'pointer', fontSize: '0.625rem', fontWeight: 600,
        transition: 'all 0.15s', ...style,
      }}
    >
      {copied ? <Check size={size} /> : <Copy size={size} />}
      {label && <span>{copied ? 'Copied' : label}</span>}
    </button>
  );
}

/* ─── Syntax-Highlighted Code Block ─── */
export function HighlightedCodeBlock({ code, language, index }) {
  const lang = resolveLang(language);
  const grammar = Prism.languages[lang];
  let html = '';
  if (grammar) {
    try { html = Prism.highlight(code, grammar, lang); } catch { html = ''; }
  }

  return (
    <div data-testid={`code-block-${index}`} style={{ position: 'relative', margin: '0.5rem 0', borderRadius: '0.5rem', overflow: 'hidden', border: '1px solid #30363d' }}>
      {/* Header bar */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0.25rem 0.625rem', background: '#161b22', borderBottom: '1px solid #30363d',
      }}>
        <span style={{ fontSize: '0.625rem', color: '#8b949e', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.04em' }}>
          {lang !== 'text' ? lang : ''}
        </span>
        <CopyButton text={code} size={11} label="Copy" />
      </div>
      {/* Code */}
      <pre style={{
        background: '#0d1117', color: '#e6edf3', padding: '0.75rem', margin: 0,
        fontSize: '0.75rem', overflow: 'auto', maxHeight: '400px', lineHeight: 1.55,
        whiteSpace: 'pre-wrap', fontFamily: '"JetBrains Mono", "Fira Code", Consolas, monospace',
      }}>
        {html ? (
          <code dangerouslySetInnerHTML={{ __html: html }} />
        ) : (
          <code>{code}</code>
        )}
      </pre>
    </div>
  );
}

/* ─── Enhanced Markdown Renderer ─── */
export function renderEnhancedMarkdown(text) {
  if (!text) return null;
  const lines = text.split('\n');
  const elements = [];
  let i = 0;
  let listItems = [];
  let listType = null;
  let codeBlockIdx = 0;

  const flushList = () => {
    if (listItems.length > 0) {
      const Tag = listType === 'ol' ? 'ol' : 'ul';
      elements.push(<Tag key={`list-${elements.length}`} style={{ margin: '0.25rem 0', paddingLeft: '1.25rem' }}>{listItems}</Tag>);
      listItems = [];
      listType = null;
    }
  };

  const renderInline = (str) => {
    const parts = [];
    let remaining = str;
    let key = 0;
    const regex = /(\*\*(.+?)\*\*|`([^`]+)`|\[([^\]]+)\]\(([^)]+)\))/g;
    let lastIndex = 0;
    let match;
    while ((match = regex.exec(remaining)) !== null) {
      if (match.index > lastIndex) parts.push(<span key={key++}>{remaining.slice(lastIndex, match.index)}</span>);
      if (match[2]) parts.push(<strong key={key++} style={{ color: '#ffffff', fontWeight: 700 }}>{match[2]}</strong>);
      else if (match[3]) parts.push(<code key={key++} style={{ background: '#1e293b', color: '#f1f5f9', padding: '0.125rem 0.4375rem', borderRadius: '0.25rem', fontSize: '0.8125em', fontFamily: '"JetBrains Mono", "Fira Code", monospace', border: '1px solid rgba(255,255,255,0.12)' }}>{match[3]}</code>);
      else if (match[4] && match[5]) parts.push(<a key={key++} href={match[5]} target="_blank" rel="noopener noreferrer" style={{ color: '#60a5fa', textDecoration: 'underline' }}>{match[4]}</a>);
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < remaining.length) parts.push(<span key={key++}>{remaining.slice(lastIndex)}</span>);
    return parts.length > 0 ? parts : str;
  };

  while (i < lines.length) {
    const line = lines[i];

    // Code block
    if (line.trimStart().startsWith('```')) {
      flushList();
      const lang = line.trim().slice(3).trim();
      const codeLines = [];
      i++;
      while (i < lines.length && !lines[i].trimStart().startsWith('```')) {
        codeLines.push(lines[i]);
        i++;
      }
      i++; // skip closing ```
      elements.push(
        <HighlightedCodeBlock key={`code-${elements.length}`} code={codeLines.join('\n')} language={lang} index={codeBlockIdx++} />
      );
      continue;
    }

    // Headings
    const headingMatch = line.match(/^(#{1,3})\s+(.+)/);
    if (headingMatch) {
      flushList();
      const level = headingMatch[1].length;
      const sizes = { 1: '1rem', 2: '0.9375rem', 3: '0.875rem' };
      elements.push(<div key={`h-${elements.length}`} style={{ fontSize: sizes[level], fontWeight: level === 1 ? 700 : 600, margin: '0.625rem 0 0.25rem', color: '#ffffff' }}>{renderInline(headingMatch[2])}</div>);
      i++;
      continue;
    }

    // Unordered list
    if (line.match(/^\s*[-*+]\s+/)) {
      if (listType !== 'ul') flushList();
      listType = 'ul';
      listItems.push(<li key={`li-${listItems.length}`} style={{ margin: '0.125rem 0', lineHeight: 1.6, color: '#f1f5f9' }}>{renderInline(line.replace(/^\s*[-*+]\s+/, ''))}</li>);
      i++;
      continue;
    }

    // Ordered list
    if (line.match(/^\s*\d+\.\s+/)) {
      if (listType !== 'ol') flushList();
      listType = 'ol';
      listItems.push(<li key={`li-${listItems.length}`} style={{ margin: '0.125rem 0', lineHeight: 1.6, color: '#f1f5f9' }}>{renderInline(line.replace(/^\s*\d+\.\s+/, ''))}</li>);
      i++;
      continue;
    }

    // Blockquote
    if (line.startsWith('>')) {
      flushList();
      elements.push(<blockquote key={`bq-${elements.length}`} style={{ borderLeft: '3px solid hsl(var(--accent))', paddingLeft: '0.75rem', margin: '0.5rem 0', color: 'hsl(var(--muted-foreground))', fontStyle: 'italic' }}>{renderInline(line.slice(1).trim())}</blockquote>);
      i++;
      continue;
    }

    // HR
    if (line.match(/^---+$/)) {
      flushList();
      elements.push(<hr key={`hr-${elements.length}`} style={{ border: 'none', borderTop: '1px solid hsl(var(--border))', margin: '0.5rem 0' }} />);
      i++;
      continue;
    }

    // Empty line
    if (line.trim() === '') {
      flushList();
      i++;
      continue;
    }

    // Regular paragraph
    flushList();
    elements.push(<p key={`p-${elements.length}`} style={{ margin: '0.25rem 0', lineHeight: 1.6 }}>{renderInline(line)}</p>);
    i++;
  }

  flushList();
  return elements;
}

/* ─── Expandable Message Wrapper ─── */
const COLLAPSE_THRESHOLD = 600; // characters

export function ExpandableMessage({ content, children }) {
  const [expanded, setExpanded] = useState(false);
  const isLong = content && content.length > COLLAPSE_THRESHOLD;

  if (!isLong) return <>{children}</>;

  return (
    <div data-testid="expandable-message">
      <div style={{
        maxHeight: expanded ? 'none' : '200px',
        overflow: 'hidden',
        position: 'relative',
        transition: 'max-height 0.3s ease',
      }}>
        {children}
        {!expanded && (
          <div style={{
            position: 'absolute', bottom: 0, left: 0, right: 0, height: '60px',
            background: 'linear-gradient(transparent, #0f1219)',
            pointerEvents: 'none',
          }} />
        )}
      </div>
      <button
        onClick={() => setExpanded(!expanded)}
        data-testid="expand-collapse-btn"
        style={{
          display: 'flex', alignItems: 'center', gap: '0.375rem',
          padding: '0.375rem 0.75rem', borderRadius: '0.375rem',
          background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)',
          color: '#818cf8', fontSize: '0.75rem', fontWeight: 600,
          cursor: 'pointer', marginTop: '0.375rem', transition: 'all 0.15s',
        }}
      >
        {expanded ? <><ChevronUp size={12} /> Show less</> : <><ChevronDown size={12} /> Show more ({Math.round(content.length / 100) * 100}+ chars)</>}
      </button>
    </div>
  );
}

/* ─── Message Action Bar (copy, regenerate, edit) ─── */
export function MessageActions({ content, onRegenerate, onEdit, isUser, isLast }) {
  return (
    <div data-testid="message-actions" style={{
      display: 'flex', alignItems: 'center', gap: '0.25rem',
      marginTop: '0.5rem', opacity: 0.6, transition: 'opacity 0.15s',
    }}
    onMouseEnter={e => e.currentTarget.style.opacity = 1}
    onMouseLeave={e => e.currentTarget.style.opacity = 0.6}
    >
      <CopyButton text={content} size={11} label="Copy" style={{ fontSize: '0.625rem' }} />
      {!isUser && onRegenerate && isLast && (
        <button
          onClick={onRegenerate}
          data-testid="regenerate-btn"
          title="Regenerate response"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
            padding: '0.1875rem 0.4375rem', borderRadius: '0.25rem', border: 'none',
            background: 'rgba(255,255,255,0.06)', color: '#94a3b8',
            cursor: 'pointer', fontSize: '0.625rem', fontWeight: 600, transition: 'all 0.15s',
          }}
        >
          <RefreshCw size={11} /> Regenerate
        </button>
      )}
      {isUser && onEdit && (
        <button
          onClick={onEdit}
          data-testid="edit-message-btn"
          title="Edit & re-run"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
            padding: '0.1875rem 0.4375rem', borderRadius: '0.25rem', border: 'none',
            background: 'rgba(255,255,255,0.06)', color: '#94a3b8',
            cursor: 'pointer', fontSize: '0.625rem', fontWeight: 600, transition: 'all 0.15s',
          }}
        >
          <Pencil size={11} /> Edit
        </button>
      )}
    </div>
  );
}

/* ─── Inline Edit Form ─── */
export function InlineEditForm({ originalContent, onSave, onCancel }) {
  const [editText, setEditText] = useState(originalContent);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 200) + 'px';
    }
  }, []);

  return (
    <div data-testid="inline-edit-form" style={{
      padding: '0.75rem', background: 'rgba(99,102,241,0.06)',
      border: '1px solid rgba(99,102,241,0.2)', borderRadius: '0.75rem',
    }}>
      <textarea
        ref={textareaRef}
        value={editText}
        onChange={(e) => {
          setEditText(e.target.value);
          e.target.style.height = 'auto';
          e.target.style.height = Math.min(e.target.scrollHeight, 200) + 'px';
        }}
        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSave(editText); } if (e.key === 'Escape') onCancel(); }}
        style={{
          width: '100%', resize: 'none', minHeight: '40px', maxHeight: '200px',
          fontSize: '0.875rem', padding: '0.5rem 0.75rem',
          border: '1px solid rgba(255,255,255,0.1)', borderRadius: '0.5rem',
          background: '#161b26', color: '#e2e8f0', lineHeight: 1.5,
          outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
        }}
        data-testid="inline-edit-textarea"
      />
      <div style={{ display: 'flex', gap: '0.375rem', marginTop: '0.5rem', justifyContent: 'flex-end' }}>
        <button
          onClick={onCancel}
          style={{
            padding: '0.375rem 0.75rem', borderRadius: '0.375rem', border: '1px solid rgba(255,255,255,0.1)',
            background: 'transparent', color: '#94a3b8', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
          }}
          data-testid="inline-edit-cancel"
        >Cancel</button>
        <button
          onClick={() => onSave(editText)}
          disabled={!editText.trim()}
          style={{
            padding: '0.375rem 0.75rem', borderRadius: '0.375rem', border: 'none',
            background: '#6366f1', color: 'white', fontSize: '0.75rem', fontWeight: 600,
            cursor: editText.trim() ? 'pointer' : 'not-allowed', opacity: editText.trim() ? 1 : 0.5,
          }}
          data-testid="inline-edit-save"
        >Save & Submit</button>
      </div>
    </div>
  );
}

/* ─── Stop Generation Button ─── */
export function StopButton({ onStop }) {
  return (
    <button
      onClick={onStop}
      data-testid="stop-generation-btn"
      style={{
        display: 'flex', alignItems: 'center', gap: '0.375rem',
        padding: '0.5rem 1rem', borderRadius: '9999px',
        background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
        color: '#f87171', fontSize: '0.75rem', fontWeight: 600,
        cursor: 'pointer', transition: 'all 0.15s',
        margin: '0.5rem auto',
      }}
    >
      <Square size={12} fill="#f87171" /> Stop generating
    </button>
  );
}

/* ─── Message Status Badge ─── */
export function MessageStatusBadge({ status }) {
  const configs = {
    streaming: { color: '#818cf8', bg: 'rgba(99,102,241,0.1)', label: 'Streaming', dot: true },
    complete: { color: '#10b981', bg: 'rgba(16,185,129,0.1)', label: 'Complete', dot: false },
    failed: { color: '#ef4444', bg: 'rgba(239,68,68,0.1)', label: 'Failed', dot: false },
    edited: { color: '#f59e0b', bg: 'rgba(245,158,11,0.1)', label: 'Edited', dot: false },
  };
  const cfg = configs[status];
  if (!cfg) return null;

  return (
    <span data-testid={`msg-status-${status}`} style={{
      display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
      fontSize: '0.5625rem', fontWeight: 600, padding: '0.0625rem 0.375rem',
      borderRadius: '0.25rem', background: cfg.bg, color: cfg.color,
    }}>
      {cfg.dot && <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: cfg.color, animation: 'pulse 1.5s infinite' }} />}
      {cfg.label}
    </span>
  );
}

/* ─── Prompt Templates Panel ─── */
const PROMPT_TEMPLATES = [
  { icon: Sparkles, label: 'Add a new feature', prompt: 'Add a new feature: ', category: 'build' },
  { icon: Code2, label: 'Explain the architecture', prompt: 'Explain the architecture of this project. Focus on the tech stack, file structure, and data flow.', category: 'understand' },
  { icon: AlertTriangle, label: 'Help debug an issue', prompt: 'I\'m seeing an issue: ', category: 'debug' },
  { icon: Wrench, label: 'Refactor code', prompt: 'Refactor the following code to improve readability and performance: ', category: 'improve' },
  { icon: BookOpen, label: 'Write documentation', prompt: 'Write comprehensive documentation for ', category: 'docs' },
  { icon: Search, label: 'Code review', prompt: 'Review the code for potential issues, security vulnerabilities, and improvement opportunities.', category: 'review' },
  { icon: RefreshCw, label: 'Optimize performance', prompt: 'Analyze and optimize the performance of ', category: 'improve' },
  { icon: AlertTriangle, label: 'Fix a specific error', prompt: 'I\'m getting this error: ', category: 'debug' },
];

export function PromptTemplatesPanel({ onSelect, onClose }) {
  const [filter, setFilter] = useState('');
  const filtered = filter
    ? PROMPT_TEMPLATES.filter(t => t.label.toLowerCase().includes(filter.toLowerCase()) || t.category.includes(filter.toLowerCase()))
    : PROMPT_TEMPLATES;

  return (
    <div data-testid="prompt-templates-panel" style={{
      position: 'absolute', bottom: '100%', left: 0, right: 0,
      marginBottom: '0.5rem', padding: '0.75rem',
      background: '#1a1f2e', border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: '0.75rem', boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
      zIndex: 50, maxHeight: '300px', overflow: 'auto',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.625rem' }}>
        <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: '#e2e8f0' }}>Prompt Templates</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', padding: '0.125rem' }} data-testid="close-templates">
          <X size={14} />
        </button>
      </div>
      <input
        placeholder="Filter templates..."
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        style={{
          width: '100%', padding: '0.375rem 0.625rem', fontSize: '0.75rem',
          background: '#0f1219', border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: '0.375rem', color: '#e2e8f0', outline: 'none',
          marginBottom: '0.5rem', boxSizing: 'border-box',
        }}
        data-testid="template-filter-input"
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
        {filtered.map((t, i) => (
          <button
            key={i}
            onClick={() => { onSelect(t.prompt); onClose(); }}
            data-testid={`template-${i}`}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem',
              padding: '0.5rem 0.625rem', borderRadius: '0.375rem',
              background: 'transparent', border: 'none', color: '#e2e8f0',
              cursor: 'pointer', fontSize: '0.75rem', textAlign: 'left',
              transition: 'background 0.12s', width: '100%',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(99,102,241,0.08)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            <t.icon size={14} style={{ color: '#818cf8', flexShrink: 0 }} />
            <span>{t.label}</span>
            <span style={{ fontSize: '0.625rem', color: '#64748b', marginLeft: 'auto', textTransform: 'capitalize' }}>{t.category}</span>
          </button>
        ))}
        {filtered.length === 0 && (
          <div style={{ padding: '0.75rem', textAlign: 'center', color: '#64748b', fontSize: '0.75rem' }}>No matching templates</div>
        )}
      </div>
    </div>
  );
}

/* ─── Draft persistence via localStorage ─── */
const DRAFT_PREFIX = 'cf_draft_';

export function saveDraft(projectId, text) {
  if (!projectId) return;
  try {
    if (text && text.trim()) {
      localStorage.setItem(`${DRAFT_PREFIX}${projectId}`, text);
    } else {
      localStorage.removeItem(`${DRAFT_PREFIX}${projectId}`);
    }
  } catch {}
}

export function loadDraft(projectId) {
  if (!projectId) return '';
  try {
    return localStorage.getItem(`${DRAFT_PREFIX}${projectId}`) || '';
  } catch { return ''; }
}

export function clearDraft(projectId) {
  if (!projectId) return;
  try { localStorage.removeItem(`${DRAFT_PREFIX}${projectId}`); } catch {}
}
